import api from "../config/api";
import type { ApiResponse, PaginatedResponse, User } from "../types";

// ==================== INTERFACES ====================

export interface CreateEmployeeRequest {
  employeeId?: string; // Optional - ສ້າງອັດຕະໂນມັດຖ້າບໍ່ມີ
  name: string;
  phone: string;
  email?: string;
  password: string;
  role?: 'admin' | 'cashier' | 'therapist';
  startDate?: string; // ISO date string
  baseSalary?: number;
  workDaysPerMonth?: number;
  workHoursPerDay?: number;
  startWorkTime?: string;
  endWorkTime?: string;
  breakTimeMinutes?: number;
  commissionRate?: number;
  overtimeRate?: number;
}

export interface UpdateEmployeeRequest {
  name?: string;
  phone?: string;
  email?: string;
  role?: 'admin' | 'cashier' | 'therapist';
  status?: 'active' | 'inactive' | 'suspended';
  startDate?: string;
  baseSalary?: number;
  workDaysPerMonth?: number;
  workHoursPerDay?: number;
  startWorkTime?: string;
  endWorkTime?: string;
  breakTimeMinutes?: number;
  commissionRate?: number;
  overtimeRate?: number;
}

export interface GetEmployeesParams {
  page?: number;
  limit?: number;
  role?: string;
  status?: string;
  search?: string;
}

export interface UpdateStatusRequest {
  status: 'active' | 'inactive' | 'suspended';
}

export interface UpdatePasswordRequest {
  password: string;
}

export interface EmployeeCommission {
  totalServices: number;
  totalCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  customerSatisfaction: number;
  activeCustomers: number;
  monthlyPerformance: {
    month: string;
    services: number;
    commission: number;
  }[];
}

export interface EmployeeStats {
  totalServices: number;
  totalCommission: {
    LAK: number;
    THB: number;
    USD: number;
  };
  customerSatisfaction: number;
  activeCustomers: number;
  monthlyPerformance: {
    month: string;
    services: number;
    commission: number;
  }[];
}

// ==================== SERVICE ====================

export const employeeService = {
  // ==================== CRUD OPERATIONS (ເກົ່າ) ====================
  
  getEmployees: async (params: GetEmployeesParams = {}): Promise<PaginatedResponse<User>> => {
    const response = await api.get('/employees', { params });
    return response.data;
  },

  getEmployee: async (id: string): Promise<ApiResponse<User>> => {
    const response = await api.get(`/employees/${id}`);
    return response.data;
  },

  createEmployee: async (data: CreateEmployeeRequest): Promise<ApiResponse<User>> => {
    const response = await api.post('/employees', data);
    return response.data;
  },

  updateEmployee: async (id: string, data: UpdateEmployeeRequest): Promise<ApiResponse<User>> => {
    const response = await api.put(`/employees/${id}`, data);
    return response.data;
  },

  deleteEmployee: async (id: string): Promise<ApiResponse<User>> => {
    const response = await api.delete(`/employees/${id}`);
    return response.data;
  },

  getEmployeeStats: async (id: string): Promise<ApiResponse<EmployeeStats>> => {
    const response = await api.get(`/employees/${id}/stats`);
    return response.data;
  },

  changePassword: async (id: string, newPassword: string): Promise<ApiResponse<null>> => {
    const response = await api.put(`/employees/${id}/password`, { password: newPassword });
    return response.data;
  },

  getEmployeeCommissions: async (id: string): Promise<ApiResponse<EmployeeCommission>> => {
    const response = await api.get(`/employees/${id}/commissions`);
    return response.data;
  },

  // ==================== NEW OPERATIONS ====================

  /**
   * ອັບເດດສະຖານະພະນັກງານ
   * @param id - Employee ID
   * @param data - Status data
   * @returns Updated employee
   */
  updateEmployeeStatus: async (
    id: string, 
    data: UpdateStatusRequest
  ): Promise<ApiResponse<User>> => {
    const response = await api.patch(`/employees/${id}/status`, data);
    return response.data;
  },

  /**
   * ເປີ່ຍນລະຫັດຜ່ານພະນັກງານ
   * @param id - Employee ID
   * @param data - Password data
   * @returns Success message
   */
  updateEmployeePassword: async (
    id: string, 
    data: UpdatePasswordRequest
  ): Promise<ApiResponse<null>> => {
    const response = await api.patch(`/employees/${id}/password`, data);
    return response.data;
  },
};